"use client";

import type { LessonNotebookState } from "@/hooks/useLessonNotebook";
import type { SuggestedQuestion } from "@/types/branching-canvas";
import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import styles from "./LessonNotebook.module.css";

/** Questions the learner can ask next; its own section so it stays when the notebook is hidden. */
export function AskChicky({
  suggestions,
  onAsk,
}: {
  suggestions: SuggestedQuestion[];
  /** Sends a suggested question to the tutor as the learner's typed turn. */
  onAsk: (question: string) => void;
}) {
  const headingId = useId();
  if (!suggestions.length) return null;
  return (
    <section aria-labelledby={headingId} className={styles.askSection} data-ask-chicky>
      <h2 id={headingId} className={styles.subtitle}>
        Ask Chicky
      </h2>
      <div className={styles.asks}>
        {suggestions.map((suggestion) => (
          <button
            key={suggestion.text}
            type="button"
            className={styles.ask}
            onClick={() => onAsk(suggestion.text)}
          >
            {suggestion.text}
          </button>
        ))}
      </div>
    </section>
  );
}

export function LessonNotebook({
  notebook,
  analyzing = false,
}: {
  notebook: LessonNotebookState;
  analyzing?: boolean;
}) {
  const headingId = useId();
  const bodyId = useId();
  const [hidden, setHidden] = useState(false);
  const { phrases, weakAreas } = notebook;
  const empty = !phrases.length && !weakAreas.length;
  return (
    <section aria-labelledby={headingId} className={styles.notebook} data-lesson-notebook>
      <div className={styles.headerRow}>
        <h2 id={headingId} className={styles.title}>
          Notebook
        </h2>
        {analyzing ? (
          <span className={styles.muted} role="status">
            Updating…
          </span>
        ) : null}
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={!hidden}
          aria-controls={bodyId}
          aria-label={hidden ? "Show notebook" : "Hide notebook"}
          title={hidden ? "Show notebook" : "Hide notebook"}
          onClick={() => setHidden((value) => !value)}
        >
          <ChevronDown
            size={18}
            aria-hidden="true"
            className={styles.chevron}
            data-open={hidden ? undefined : true}
          />
        </button>
      </div>
      <div id={bodyId} className={styles.body} hidden={hidden}>
        {empty ? (
          <p className={styles.muted}>
            Phrases Chicky teaches and your board practice collect here.
          </p>
        ) : null}
        {phrases.length ? (
          <ul className={styles.list}>
            {[...phrases].reverse().map((phrase) => (
              <li key={phrase.key} className={styles.phrase}>
                <p className={styles.phraseText}>{phrase.text}</p>
                {phrase.translation ? (
                  <p className={styles.translation}>{phrase.translation}</p>
                ) : null}
                {phrase.needsReview || phrase.concepts.length ? (
                  <p className={styles.tags}>
                    {phrase.needsReview ? (
                      <span className={styles.review}>Worth another look</span>
                    ) : null}
                    {phrase.concepts.map((concept) => (
                      <span key={concept} className={styles.tag}>
                        {concept}
                      </span>
                    ))}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {weakAreas.length ? (
          <>
            <h3 className={styles.subtitle}>Keep practising</h3>
            <ul className={styles.list}>
              {weakAreas.map((area) => (
                <li key={area.concept} className={styles.weak}>
                  <span className={styles.weakConcept}>{area.concept}</span>
                  {area.suggestion ? <span className={styles.muted}>{area.suggestion}</span> : null}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </section>
  );
}
